export const formatEmployeeId = (value) => {
  if (!value) return '—';
  const clean = value.toString().trim();
  const match = clean.match(/^([A-Za-z]+)?\s*:?(\d+)$/);
  if (match) {
    const prefix = (match[1] || 'EMP').toUpperCase();
    const digits = match[2].replace(/^0+/, '') || '0';
    return `${prefix}${digits.padStart(3, '0')}`;
  }
  return clean || '—';
};

const toDateKey = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const parseTime = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const filterUsersByDepartment = (users, department) => {
  if (!department || department === 'all') return users;
  return users.filter(
    (user) =>
      String(user.department || user.departmentName || '')
        .trim()
        .toLowerCase() === department.toLowerCase()
  );
};

export const getDepartmentOptions = (users) => {
  const set = new Set();
  users.forEach((user) => {
    const dept = String(user.department || user.departmentName || '').trim();
    if (dept) set.add(dept);
  });
  return Array.from(set).sort();
};

export const isOnApprovedLeave = (leaves, userId, dateKey) => {
  const day = new Date(`${dateKey}T12:00:00`);
  return leaves.some((leave) => {
    if (leave.status !== 'approved') return false;
    const userMatch =
      String(leave.user?._id || leave.user || '') === String(userId);
    if (!userMatch) return false;
    const from = new Date(leave.fromDate);
    const to = new Date(leave.toDate);
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    return day >= from && day <= to;
  });
};

export const buildAttendanceReportRows = ({
  records = [],
  users = [],
  dateRange = {},
  department = 'all',
  lateAfterHour = 9,
  lateAfterMinute = 30
}) => {
  const start = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
  const end = dateRange.end ? new Date(`${dateRange.end}T23:59:59`) : null;

  const filteredUsers = filterUsersByDepartment(users, department);
  const userMap = new Map(filteredUsers.map((u) => [String(u._id), u]));

  const inRange = records.filter((record) => {
    const ts = parseTime(record.timestamp);
    if (!ts) return false;
    if (start && ts < start) return false;
    if (end && ts > end) return false;
    const userId = String(record.user?._id || record.userId || record.user || '');
    return userMap.has(userId);
  });

  const grouped = new Map();

  inRange.forEach((record) => {
    const userId = String(record.user?._id || record.userId || record.user || '');
    const dateKey = toDateKey(record.timestamp);
    const key = `${userId}-${dateKey}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        userId,
        dateKey,
        checkIns: [],
        checkOuts: []
      });
    }
    const bucket = grouped.get(key);
    if (record.type === 'check-in') bucket.checkIns.push(record);
    if (record.type === 'check-out') bucket.checkOuts.push(record);
  });

  const rows = [];

  grouped.forEach((bucket) => {
    const user = userMap.get(bucket.userId) || {};
    const checkInRecord = bucket.checkIns.sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    )[0];
    const checkOutRecord = bucket.checkOuts.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    )[0];

    const checkInTime = checkInRecord ? parseTime(checkInRecord.timestamp) : null;
    const checkOutTime = checkOutRecord ? parseTime(checkOutRecord.timestamp) : null;

    let hours = 0;
    if (checkInTime && checkOutTime && checkOutTime > checkInTime) {
      hours = (checkOutTime - checkInTime) / (1000 * 60 * 60);
    }

    let status = 'present';
    if (checkInTime) {
      const lateThreshold = new Date(checkInTime);
      lateThreshold.setHours(lateAfterHour, lateAfterMinute, 0, 0);
      if (checkInTime > lateThreshold) status = 'late';
    } else if (bucket.checkOuts.length) {
      status = 'incomplete';
    }

    rows.push({
      id: `${bucket.userId}-${bucket.dateKey}`,
      employee: user.name || recordName(checkInRecord, checkOutRecord),
      employeeId: formatEmployeeId(user.employeeId || user.employeeCode),
      department: user.department || user.departmentName || '—',
      date: bucket.dateKey,
      checkIn: checkInTime
        ? checkInTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
        : '—',
      checkOut: checkOutTime
        ? checkOutTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
        : '—',
      hours: hours ? hours.toFixed(1) : '—',
      status,
      userId: bucket.userId
    });
  });

  return rows.sort((a, b) => {
    if (a.date !== b.date) return b.date.localeCompare(a.date);
    return a.employee.localeCompare(b.employee);
  });
};

const recordName = (checkInRecord, checkOutRecord) =>
  checkInRecord?.user?.name ||
  checkOutRecord?.user?.name ||
  checkInRecord?.employeeName ||
  checkOutRecord?.employeeName ||
  '—';

export const buildAttendanceStats = ({
  rows = [],
  users = [],
  leaves = [],
  dateRange = {},
  department = 'all'
}) => {
  const filteredUsers = filterUsersByDepartment(users, department);
  const endDate = dateRange.end || dateRange.start || new Date().toISOString().split('T')[0];

  const presentIds = new Set(rows.filter((r) => r.status !== 'incomplete').map((r) => r.userId));
  const lateCount = rows.filter((r) => r.status === 'late').length;

  const onLeaveIds = new Set();
  filteredUsers.forEach((user) => {
    if (isOnApprovedLeave(leaves, user._id, endDate)) {
      onLeaveIds.add(String(user._id));
    }
  });

  const absentCount = filteredUsers.filter((user) => {
    const id = String(user._id);
    return !presentIds.has(id) && !onLeaveIds.has(id);
  }).length;

  return {
    totalEmployees: filteredUsers.length,
    present: presentIds.size,
    absent: absentCount,
    late: lateCount,
    onLeave: onLeaveIds.size
  };
};

export const exportAttendanceCsv = (rows) => {
  const header = ['Employee', 'Employee ID', 'Department', 'Date', 'Check In', 'Check Out', 'Hours', 'Status'];
  const lines = rows.map((row) =>
    [row.employee, row.employeeId, row.department, row.date, row.checkIn, row.checkOut, row.hours, row.status]
      .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
      .join(',')
  );
  const csv = [header.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `attendance-report-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
